import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  FormControl,
  TextField,
  Modal,
} from "@mui/material";
import React, { forwardRef, useEffect, useState } from "react";
import SearchIcon from "@material-ui/icons/Search";
import { styled } from "@mui/material/styles";
import ApiService from "./ApiService";
import MaterialTable from "./components/MaterialTable";
import {
  AddBox,
  ArrowDownward,
  ChevronLeft,
  ChevronRight,
  Clear,
  Edit,
  FilterList,
  FirstPage,
  LastPage,
  Remove,
  SaveAlt,
  Search,
  ViewColumn,
} from "@mui/icons-material";
import Button from "./components/Custom/Button/Button";
import { Typography } from "@material-ui/core";

const StyledBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  // minHeight: "100vh",
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  color: theme.palette.text.primary,
  boxShadow: theme.shadows[3],
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  height: 37,
  fontSize: 14,
  marginBottom: 2,
  textTransform: "capitalize",
  borderRadius: theme.shape.borderRadius,
  [theme.breakpoints.down("sm")]: {
    fontSize: 12,
    height: 36,
  },
  [theme.breakpoints.down("xs")]: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
}));

const MenuUsage = () => {
  const [failed, setFailed] = useState(false);
  const [success, setSuccess] = useState(false);
  const [responseMessage, setResponseMessage] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [users, setUsers] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (success || failed) {
      const timer = setTimeout(() => {
        setSuccess(false);
        setFailed(false);
        setResponseMessage(null);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [success, failed]);

  const fetchUsers = () => {
    ApiService.fetchUsers().then((response) => {
      setUsers(response.data);
    });
  };

  const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    let month = "" + (d.getMonth() + 1);
    let day = "" + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    return [year, month, day].join("");
  };

  const handleSearch = () => {
    let message = "";
    if (!selectedUser && !fromDate && !toDate) {
      message = "Please select a user and both dates.";
    } else if (!selectedUser) {
      message = "Please select a user.";
    } else if (!fromDate && !toDate) {
      message = "Please select both dates.";
    } else if (!fromDate) {
      message = "Please select a From Date.";
    } else if (!toDate) {
      message = "Please select a To Date.";
    }

    if (message) {
      setFailed(true);
      setSuccess(false);
      setResponseMessage(message);
      return;
    }

    if (selectedUser && fromDate && toDate) {
      setLoading(true);
      const formattedFromDate = formatDate(fromDate);
      const formattedToDate = formatDate(toDate);
      ApiService.fetchUserMenuUsage(
        selectedUser.id,
        formattedFromDate,
        formattedToDate
      )
        .then((response) => {
          if (response.data.status === "OK") {
            setData(response.data.responseObject);
            setSuccess(true);
            setFailed(false);
            setResponseMessage(
              `Search successful for ${selectedUser.userName} from ${fromDate} to ${toDate}`
            );
          } else {
            setData([]);
            setSuccess(false);
            setFailed(true);
            setResponseMessage(response.data.responseMessage);
          }
        })
        .catch((error) => {
          setData([]);
          setFailed(true);
          setSuccess(false);
          setResponseMessage("Error fetching menu usage.");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const columns = [
    { title: "Menu Name", field: "menuText" },
    { title: "Count", field: "count" },
    { title: "Module Type", field: "module" },
  ];

  const tableIcons = {
    Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),

    Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),

    Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
    Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
    Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
    FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
    LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
    NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    PreviousPage: forwardRef((props, ref) => (
      <ChevronLeft {...props} ref={ref} />
    )),
    ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
    SortArrow: forwardRef((props, ref) => (
      <ArrowDownward {...props} ref={ref} />
    )),
    ThirdStateCheck: forwardRef((props, ref) => (
      <Remove {...props} ref={ref} />
    )),
    ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
  };

  const loadingModalStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return (
    <StyledBox>
      <Modal open={loading} style={loadingModalStyle}>
        <Box>
          <CircularProgress color="primary" />
        </Box>
      </Modal>
      <Typography component="h2" variant="h5" color="primary" gutterBottom>
        Menu Usage
      </Typography>
      {failed && (
        <Alert severity="error" sx={{ mt: 1 }} elevation={0}>
          {responseMessage}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mt: 1 }} elevation={0}>
          {responseMessage}
        </Alert>
      )}
      <Divider light sx={{ my: 2 }} />

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: "1",
            minWidth: "180px",
            maxWidth: { xs: "100%", sm: "200px", md: "200px" },
            marginRight: { sm: 2 },
          }}
        >
          <FormControl fullWidth>
            <Autocomplete
              options={users}
              getOptionLabel={(option) => option.userName}
              value={selectedUser}
              onChange={(event, newValue) => {
                setSelectedUser(newValue);
              }}
              size="small"
              sx={{
                width: "100%",
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select User" variant="outlined" />
              )}
              clearOnEscape
            />
          </FormControl>
        </Box>
        <Box
          sx={{
            flex: "1",
            minWidth: "180px",
            maxWidth: { xs: "100%", sm: "200px", md: "200px" },
            marginRight: { sm: 2 },
          }}
        >
          <FormControl fullWidth>
            <TextField
              type="date"
              label="From Date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              size="small"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </FormControl>
        </Box>
        <Box
          sx={{
            flex: "1",
            minWidth: "180px",
            maxWidth: { xs: "100%", sm: "200px", md: "200px" },
            marginRight: { sm: 2 },
          }}
        >
          <FormControl fullWidth>
            <TextField
              type="date"
              label="To Date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              size="small"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </FormControl>
        </Box>
        <Box>
          <StyledButton
            variant="contained"
            onClick={handleSearch}
            color="primary"
            size="large"
            startIcon={<SearchIcon sx={{ fontSize: 20 }} />}
          >
            Search
          </StyledButton>
        </Box>
      </Box>
      <Box sx={{ mt: 6 }}>
        <MaterialTable
          icons={tableIcons}
          columns={columns}
          data={data}
          title="Menu Usage Summary"
          options={{
            search: false,
            exportButton: false,
            pageSize: 5,
            pageSizeOptions: [5, 10, 20],
            padding: "default",
            showEmptyDataSourceMessage: true,
            doubleHorizontalScroll: true,
            selection: false,
            actionsColumnIndex: -1,
            tableLayout: "auto",
            emptyRowsWhenPaging: false,
          }}
        />
      </Box>
    </StyledBox>
  );
};

export default MenuUsage;
